import React from 'react'
import PropTypes from 'prop-types'
import { makeStyles, createStyles } from '@mui/styles'
// import { SvgIconProps } from '@mui/material/SvgIcon'

import List from '@mui/material/List'

import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Divider from '@mui/material/Divider'
import Collapse from '@mui/material/Collapse'

import IconExpandLess from '@mui/icons-material/ExpandLess'
import IconExpandMore from '@mui/icons-material/ExpandMore'

import AppMenuItemComponent from './appmenucomponent'
import { stripLang, itemHasActiveLink, isSameLink } from './menuMatch'

// React runtime PropTypes
export const AppMenuItemPropTypes = {
  name: PropTypes.string.isRequired,
  link: PropTypes.string,
  Icon: PropTypes.elementType,
  items: PropTypes.array,
}

const AppMenuItem = props => {
  const { name, link, Icon, items = [], state, disabled, onClick, activeLink } = props
  const classes = useStyles()
  const isExpandable = items && items.length > 0

  // Ativo derivado da ROTA (activeLink resolvido no topo pelo matcher):
  //  - folha/link: fica ativo quando o próprio link é o ativo (resolve #1/#3);
  //  - grupo (pai): fica "ativo-pai" quando ALGUM descendente é o ativo (resolve #1) e
  //    abre automaticamente (resolve #2).
  const isActiveSelf = !isExpandable && typeof link === 'string' && isSameLink(stripLang(link), activeLink)
  const hasActiveDescendant = isExpandable && items.some(child => itemHasActiveLink(child, activeLink))

  // open: por padrão SEGUE a rota (abre o pai do item ativo). Um clique vira override manual
  // (openOverride true/false); ao trocar de rota (activeLink muda) o override é resetado, voltando
  // a acompanhar a navegação — comportamento dos menus profissionais.
  const [openOverride, setOpenOverride] = React.useState(null)
  React.useEffect(() => { setOpenOverride(null) }, [activeLink])
  const open = openOverride === null ? hasActiveDescendant : openOverride

  function handleClick() {
    setOpenOverride(!open)
  }

  const MenuItemRoot = (
    <AppMenuItemComponent
      className={classes.menuItem}
      link={disabled ? null : link}
      onClick={onClick ? onClick : handleClick}
      state={state}
      active={isActiveSelf}
      parentActive={hasActiveDescendant}
    >
      {/* Display an icon if any */}
      {!!Icon && (
        <ListItemIcon className={classes.menuItemIcon}>
          <Icon />
        </ListItemIcon>
      )}
        <ListItemText primary={name} inset={!Icon} />
      {/* Display the expand menu if the item has children */}
      {isExpandable && !open && <IconExpandMore />}
      {isExpandable && open && <IconExpandLess />}
    </AppMenuItemComponent>
  )

  const MenuItemChildren = isExpandable ? (
    <Collapse in={open} timeout="auto" unmountOnExit>
      <Divider />
      <List component="div" disablePadding>
        {items.map((item, index) => (
          <AppMenuItem disabled={item.disabled ?? false} {...item} key={index} activeLink={activeLink} />
        ))}
      </List>
    </Collapse>
  ) : null

  return (
    <>
      {MenuItemRoot}
      {MenuItemChildren}
    </>
  )
}

const useStyles = makeStyles(theme =>
  createStyles({
    menuItem: {
      borderRadius: 'var(--radius-md)',
      margin: '0 var(--space-2) var(--space-1)',
      transition: 'var(--transition-fast)',
      color: 'var(--text-primary)',
      '&:hover': {
        background: 'var(--mexx-green-subtle)',
        color: 'var(--mexx-green-primary)',
        '& .MuiListItemIcon-root': {
          color: 'var(--mexx-green-primary)',
        },
      },
      '&.active': {
        background: 'var(--mexx-green-light)',
        color: 'var(--mexx-green-dark)',
        '& .MuiListItemIcon-root': {
          color: 'var(--mexx-green-dark)',
        },
      },
      // "Seção atual" (pai que contém a página aberta): destaque mais SUTIL que o item ativo —
      // só cor/negrito + faixa lateral, sem o fundo cheio, pra distinguir pai-ativo de filho-ativo.
      '&.active-parent': {
        color: 'var(--mexx-green-dark)',
        fontWeight: 600,
        boxShadow: 'inset 3px 0 0 var(--mexx-green-primary)',
        '& .MuiListItemIcon-root': {
          color: 'var(--mexx-green-dark)',
        },
      },
    },
    menuItemIcon: {
      color: 'var(--mexx-green-primary)',
      minWidth: '40px',
    },
  }),
)

export default AppMenuItem
